import React from 'react';
import {
  Action, ConfirmModal, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { Offer } from '../../../providers/api/dtos';
import { offerAcceptance } from '../../../providers/api/offer/offerProvider';
import { DocumentStatus, OfferDocumentTypeValue, OfferStatus } from '../../../domain/types';
import useOfferWizardData from '../../hooks/use-offer-wizard-data/useOfferWizardData';
import { useOfferDocuments } from '../../hooks/use-offer-documents/useOfferDocuments';
import { useComputedOfferStatus } from '../../hooks/use-computed-offer-status/useComputedOfferStatus';

export const ACCEPTANCE_OFFER_SIGNATURE_REQUEST_SUCCESS_MESSAGE = 'La richiesta di accettazione della proposta e presa visione è stata avviata con successo.';
export const ACCEPTANCE_OFFER_SIGNATURE_REQUEST_ERROR_MESSAGE = 'Non è stato possibile avviare la richesta di accettazione e presa visione della proposta.';

interface ChildFnProps {
  ackSignatureRequest: VoidFunction,
  isLoading: boolean,
}

export interface StartOfferAcceptanceSignatureRequestActionProps {
  offer: Offer,
  onSuccess?: VoidFunction,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
}

const StartOfferAcceptanceSignatureRequestAction: React.FC<StartOfferAcceptanceSignatureRequestActionProps> = ({
  offer,
  onSuccess,
  children,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const acceptanceSignatureRequestModal = useModal();
  const { data: offerWizard } = useOfferWizardData(offer?.intentId);
  const { data: offerDocuments } = useOfferDocuments(offer?.id);
  const { data: computedStatusData } = useComputedOfferStatus(offer?.intentId, offer);

  const hasPurchaseProposalApproved = React.useMemo(() => offerDocuments?.some(
    (doc) => doc.type === OfferDocumentTypeValue.PURCHASE_PROPOSAL && doc.status === DocumentStatus.APPROVED,
  ), [offerDocuments]);

  const openConfirmModal = React.useCallback(() => {
    acceptanceSignatureRequestModal.open();
  }, [acceptanceSignatureRequestModal]);

  const acceptanceSignatureRequest = React.useCallback(async () => {
    try {
      setIsLoading(true);

      await offerAcceptance(offer.id!);

      setIsLoading(false);
      acceptanceSignatureRequestModal.close();

      addSuccess(ACCEPTANCE_OFFER_SIGNATURE_REQUEST_SUCCESS_MESSAGE);
      onSuccess?.();
    } catch (_) {
      setIsLoading(false);
      addError(ACCEPTANCE_OFFER_SIGNATURE_REQUEST_ERROR_MESSAGE);
    }
  }, [offer, acceptanceSignatureRequestModal, addSuccess, onSuccess, addError]);

  if (computedStatusData?.status !== OfferStatus.IN_PROGRESS || !offerWizard?.isDigitallySigned || hasPurchaseProposalApproved) {
    return null;
  }

  return (
    <>
      {
        children
          ? children({ ackSignatureRequest: openConfirmModal, isLoading })
          : (
            <Action
              aria-label="Inizia la richiesta di firma digitale dell'accettazione e presa visione della proposta"
              label="Richiedi accettazione"
              onClick={openConfirmModal}
              size="S"
              loading={isLoading}
            />
          )
      }
      <Portal>
        <ConfirmModal
          {...acceptanceSignatureRequestModal}
          title="Richiedi accettazione e presa visione della proposta"
          aria-label="Richiedi accettazione e presa visione della proposta"
          onConfirm={acceptanceSignatureRequest}
          onAbort={acceptanceSignatureRequestModal.close}
          saving={isLoading}
        >
          Confermando l&apos;operazione, sarà avviata la procedura digitale di accettazione e presa visione della proposta.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default StartOfferAcceptanceSignatureRequestAction;
